'use client';
import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';

import { createGenericContext } from '@contexts/generateContext';

export interface HotelDifferenceContextType {
  diffData: any[];
  setDiffData: Dispatch<SetStateAction<any[]>>;
  addDiffData: (item: any) => void;
  removeDiffData: (index: number) => void;
  visibleInHeader: boolean;
  setVisibleInHeader: Dispatch<SetStateAction<boolean>>;
  addMultipleDiffData: (item: any[]) => void;
}

const [useHotelDifference, HotelDifferenceContextProvider] =
  createGenericContext<HotelDifferenceContextType>();

const HotelDifferenceProvider = (
  props: React.PropsWithChildren<unknown>
): React.JSX.Element => {
  const [diffData, setDiffData] = useState<any[]>(() => {
    if (typeof window !== 'undefined') {
      const storedData = localStorage.getItem('diffData');
      return storedData ? JSON.parse(storedData) : [];
    }
    return [];
  });
  const [visibleInHeader, setVisibleInHeader] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('diffData', JSON.stringify(diffData));
    }
  }, [diffData]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleStorageChange = (event: StorageEvent) => {
        if (event.key === 'diffData') {
          setDiffData(event.newValue ? JSON.parse(event.newValue) : []);
        }
      };

      window.addEventListener('storage', handleStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, []);

  const addDiffData = (item: any) => {
    setDiffData((prevData) => [...prevData, item]);
  };

  const removeDiffData = (index: number) => {
    setDiffData((prevData) =>
      prevData.filter((item) => item.hotelId !== index)
    );
  };

  const addMultipleDiffData = (item: any[]) => {
    setDiffData(item);
  };

  return (
    <HotelDifferenceContextProvider
      value={{
        diffData,
        setDiffData,
        addDiffData,
        removeDiffData,
        visibleInHeader,
        setVisibleInHeader,
        addMultipleDiffData,
      }}
    >
      {props.children}
    </HotelDifferenceContextProvider>
  );
};

export { HotelDifferenceProvider, useHotelDifference };
